import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { apiRequest } from '@/lib/queryClient';
import { Loader2 } from 'lucide-react';

interface Plot {
  id: string;
  name?: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

interface PlotSelectorProps {
  projectId: string;
  isOpen: boolean;
  onSelect: (plotId: string, plotVersionId?: string) => void;
  onClose: () => void;
}

export function PlotSelector({ projectId, isOpen, onSelect, onClose }: PlotSelectorProps) {
  const [plots, setPlots] = useState<Plot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !projectId) return;

    setLoading(true);
    setError(null);
    apiRequest('GET', `/api/projects/${projectId}/plots`)
      .then((res) => res.json())
      .then((data: Plot[]) => {
        setPlots(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load plots:', err);
        setError('Failed to load plots');
        setLoading(false);
      });
  }, [isOpen, projectId]);

  const handleSelect = (plotId: string) => {
    onSelect(plotId, undefined);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent 
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-plot-selector="true"
      >
        <DialogHeader>
          <DialogTitle>Select a plot</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {error && (
            <div className="text-sm text-destructive py-4">{error}</div>
          )}
          {!loading && !error && plots.length === 0 && (
            <div className="text-sm text-muted-foreground py-4">
              No plots found in this project.
            </div>
          )}
          {!loading && !error && plots.length > 0 && (
            <div className="grid grid-cols-1 gap-2">
              {plots.map((plot) => (
                <Card
                  key={plot.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleSelect(plot.id)}
                >
                  <CardHeader>
                    <CardTitle className="text-base">{plot.title || plot.name || 'Untitled Plot'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-xs">
                      Updated: {new Date(plot.updatedAt).toLocaleDateString()}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

